import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../api.ts";
import { Checkbox, Field, Input, Mono, CopyButton } from "../../ui/index.tsx";
import { plural } from "../../format.ts";
import type { Game, TicketPriority, TicketStatus } from "../../../shared/types.ts";

export function useGames() {
  const { data, error, loading } = useApi<Game[]>("/games");
  const games = data ?? [];
  const nameOf = useCallback((id: string) => data?.find((g) => g.id === id)?.name ?? id, [data]);
  return { games, nameOf, error, loading };
}

export function GamePicker({ games, value, onChange, loading }: { games: Game[]; value: string[]; onChange: (v: string[]) => void; loading?: boolean }) {
  if (loading) return <p className="text-sm text-muted">Loading games…</p>;
  if (!games.length) return <p className="text-sm text-muted">No games available.</p>;
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {games.map((g) => (
        <Checkbox key={g.id} label={g.name} checked={value.includes(g.id)}
          onChange={(on) => onChange(on ? [...value, g.id] : value.filter((x) => x !== g.id))} />
      ))}
    </div>
  );
}

export interface DurationValue { lifetime: boolean; days: string }
export const DEFAULT_DURATION: DurationValue = { lifetime: false, days: "30" };

/** null = lifetime, undefined = invalid input. */
export function durationDays(d: DurationValue): number | null | undefined {
  if (d.lifetime) return null;
  const n = Number(d.days);
  return Number.isInteger(n) && n >= 1 && n <= 36500 ? n : undefined;
}

export const fmtDuration = (days: number | null) => (days === null ? "Lifetime" : plural(days, "day"));

export function DurationField({ value, onChange, label = "Duration" }: { value: DurationValue; onChange: (v: DurationValue) => void; label?: string }) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-4">
        <Input type="number" min={1} max={36500} className="w-28" value={value.days} disabled={value.lifetime}
          onChange={(e) => onChange({ ...value, days: e.target.value })} aria-label="Days" />
        <span className="text-sm text-muted">days</span>
        <Checkbox label="Lifetime" checked={value.lifetime} onChange={(lifetime) => onChange({ ...value, lifetime })} />
      </div>
    </Field>
  );
}

/** Shows a generated secret once, with a copy button. */
export function SecretBox({ value }: { value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-line bg-bg px-3 py-2">
      <Mono className="break-all text-sm text-fg">{value}</Mono>
      <CopyButton text={value} label="Copy" />
    </div>
  );
}

export const TICKET_STATUS: Record<TicketStatus, { label: string; tone: "info" | "warn" | "ok" | "neutral" }> = {
  open: { label: "Open", tone: "warn" },
  in_progress: { label: "In progress", tone: "info" },
  resolved: { label: "Resolved", tone: "ok" },
  closed: { label: "Closed", tone: "neutral" },
};

export const PRIORITY_TONE: Record<TicketPriority, "neutral" | "info" | "bad"> = { low: "neutral", normal: "info", high: "bad" };

export function downloadText(filename: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Re-renders every `ms` so relative timestamps stay fresh. */
export function useTick(ms = 30_000) {
  const [, set] = useState(0);
  useEffect(() => { const id = setInterval(() => set((n) => n + 1), ms); return () => clearInterval(id); }, [ms]);
}
